/**
 * Public /claim/:token — the payment-first landing page for venues that
 * received the launch email. Shows the current listing preview, the
 * £100 Bronze upgrade CTA, and a fallback "free basic claim" form.
 *
 * Flow:
 *   1. GET /api/claim/:token           → preview + offer_active + already_claimed
 *   2a. POST /api/payments/checkout    (lookup_key=launch_bronze_2026, claim_token=…)
 *       → Stripe checkout, auto-creates partner + Bronze on webhook fulfilment
 *   2b. POST /api/claim/:token/basic-account → free basic-listing claim (pending)
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { CheckCircle2, Loader2, MapPin, Sparkles, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClaimVenuePage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, err: '', data: null });
  const [busy, setBusy] = useState(false);
  const [showBasic, setShowBasic] = useState(false);
  const [basicForm, setBasicForm] = useState({ contact_name: '', email: '' });
  const [basicOk, setBasicOk] = useState(null);
  const [payErr, setPayErr] = useState('');
  const [buyer, setBuyer] = useState({ email: '', name: '' });
  const [buyerErr, setBuyerErr] = useState('');
  const [lookupKey, setLookupKey] = useState('');

  useEffect(() => {
    axios.get(`${API}/claim/${token}`)
      .then(({ data }) => setState({ loading: false, err: '', data }))
      .catch((e) => setState({
        loading: false,
        err: e?.response?.data?.detail || 'This claim link is not recognised.',
        data: null,
      }));
    // Discover the actual Stripe lookup_key for the launch bronze product —
    // admin-created row gets a UUID-suffixed key we can't guess client-side.
    axios.get(`${API}/payments/products?category=launch_bronze`)
      .then(({ data }) => {
        const p = (data.items || [])[0];
        if (p) setLookupKey(p.lookup_key);
      })
      .catch(() => {});
  }, [token]);

  async function payLaunchBronze() {
    setPayErr(''); setBuyerErr('');
    if (!buyer.email || !buyer.email.includes('@')) {
      setBuyerErr('Please enter the email address for your new account');
      return;
    }
    if (!lookupKey) {
      setPayErr('The launch offer is not yet configured — contact us and we will sort it out.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        quantity: 1,
        origin_url: window.location.origin,
        buyer_email: buyer.email.trim(),
        buyer_name: buyer.name.trim(),
        claim_token: token,
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else setPayErr('Could not start checkout — please try again.');
    } catch (e) {
      setPayErr(e?.response?.data?.detail || 'Checkout failed. Please try again.');
      setBusy(false);
    }
  }

  async function submitBasic(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/claim/${token}/basic-account`, basicForm);
      setBasicOk(data);
    } catch (e2) {
      setBasicOk({ ok: false, error: e2?.response?.data?.detail || 'Could not create your account.' });
    } finally {
      setBusy(false);
    }
  }

  if (state.loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" size={32}/></div>;
  }

  if (state.err || !state.data) {
    return (
      <main className="max-w-2xl mx-auto px-5 py-16 text-center">
        <h1 className="font-serif text-3xl text-heritage mb-3">Link not recognised</h1>
        <p className="text-charcoal/70">{state.err}</p>
        <p className="text-sm text-charcoal/60 mt-6">If you meant to claim your GoLeicestershire listing, contact us at
          <a href="mailto:hello@goleicestershire.com" className="text-heritage underline ml-1">hello@goleicestershire.com</a>.
        </p>
      </main>
    );
  }

  const { attraction, offer_active, offer_price_gbp, offer_covers, already_claimed } = state.data;

  return (
    <main className="max-w-3xl mx-auto px-5 py-10 sm:py-14">
      <Helmet><title>{`Claim ${attraction.name} — GoLeicestershire`}</title></Helmet>

      <p className="text-xs uppercase tracking-widest text-mustard font-semibold mb-2">Launch invitation</p>
      <h1 className="font-serif text-3xl sm:text-4xl text-heritage mb-2" data-testid="claim-venue-name">
        Claim your listing for {attraction.name}
      </h1>
      {attraction.town && (
        <p className="text-charcoal/70 inline-flex items-center gap-1.5">
          <MapPin size={14}/> {attraction.town}
        </p>
      )}

      {/* Current listing preview */}
      <section className="mt-8 bg-admin/40 border border-charcoal/10 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-charcoal/70 uppercase tracking-wide mb-3">How your listing looks today</h2>
        <div className="flex gap-4">
          {attraction.hero_image && (
            <img src={attraction.hero_image} alt="" className="w-32 h-32 object-cover rounded-md shrink-0"/>
          )}
          <div className="min-w-0">
            <div className="font-serif text-xl">{attraction.name}</div>
            <div className="text-sm text-charcoal/70">{attraction.town}</div>
            <p className="text-sm text-charcoal/80 mt-2 line-clamp-3">{attraction.summary || 'No summary set yet.'}</p>
          </div>
        </div>
        {attraction.member_tier ? (
          <p className="text-xs text-charcoal/60 mt-4 italic">This venue is already a paying member ({attraction.member_tier}).</p>
        ) : (
          <p className="text-xs text-charcoal/60 mt-4 italic">Currently a basic (non-member) listing — name and town only appear publicly.</p>
        )}
      </section>

      {already_claimed ? (
        <section className="mt-8 bg-mustard/10 border border-mustard/40 rounded-lg p-6 text-center">
          <CheckCircle2 className="mx-auto text-forest mb-2" size={28}/>
          <h3 className="font-serif text-xl text-heritage mb-1">This listing has already been claimed</h3>
          <p className="text-sm text-charcoal/70">If that&apos;s you, <a href="/partners" className="text-heritage underline">sign in here</a> to edit your listing.</p>
          <p className="text-xs text-charcoal/60 mt-3">Not you? Email <a href="mailto:hello@goleicestershire.com" className="underline">hello@goleicestershire.com</a> and we&apos;ll sort it out.</p>
        </section>
      ) : offer_active ? (
        <section className="mt-8 bg-cream border border-heritage/30 rounded-lg p-6" data-testid="launch-offer-panel">
          <div className="flex items-start gap-3 mb-3">
            <Sparkles className="text-mustard shrink-0 mt-1" size={22}/>
            <div>
              <h3 className="font-serif text-2xl text-heritage">Launch offer — £{Number(offer_price_gbp).toFixed(0)}</h3>
              <p className="text-sm text-charcoal/70 mt-1">
                Upgrade to a <strong>Bronze website listing</strong> — {offer_covers}.
              </p>
            </div>
          </div>
          <ul className="text-sm text-charcoal/80 space-y-1.5 mb-5 pl-1">
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-forest shrink-0 mt-0.5"/> Full description + up to 5 photos</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-forest shrink-0 mt-0.5"/> Opening times, contact, website, ticket prices</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-forest shrink-0 mt-0.5"/> Accessibility panel visible on the public page</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-forest shrink-0 mt-0.5"/> Higher placement in Explore and Plan-My-Day results</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-forest shrink-0 mt-0.5"/> You edit the listing yourself (edits go to a moderator for approval)</li>
          </ul>
          <p className="text-xs text-charcoal/60 italic mb-4">
            This offer covers the website only. The 2026 Great Days Out printed guide is already at press.
            Renewal at standard £250/year from 1 April 2027.
          </p>

          <div className="border-t border-charcoal/10 pt-4 mt-4">
            <label className="text-xs font-semibold text-charcoal/70 block mb-1">Your email (this becomes your account login)</label>
            <input type="email" data-testid="claim-buyer-email" placeholder="you@venue.co.uk"
                   value={buyer.email} onChange={(e) => setBuyer((b) => ({ ...b, email: e.target.value }))}
                   className="w-full border border-charcoal/20 rounded px-3 py-2 mb-2 text-sm"/>
            <label className="text-xs font-semibold text-charcoal/70 block mb-1">Your name</label>
            <input type="text" data-testid="claim-buyer-name" placeholder="First Last"
                   value={buyer.name} onChange={(e) => setBuyer((b) => ({ ...b, name: e.target.value }))}
                   className="w-full border border-charcoal/20 rounded px-3 py-2 mb-3 text-sm"/>
            {buyerErr && <p className="text-xs text-heritage mb-2">{buyerErr}</p>}
            {payErr && <p className="text-xs text-heritage mb-2">{payErr}</p>}
            <button onClick={payLaunchBronze} disabled={busy} data-testid="claim-pay-launch-bronze"
                    className="w-full bg-heritage text-cream py-3 rounded-full font-semibold hover:bg-heritage/90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <><Loader2 className="animate-spin" size={16}/> Redirecting to Stripe…</> : 'Pay £100 & unlock Bronze →'}
            </button>
          </div>
        </section>
      ) : (
        <section className="mt-8 bg-admin/40 border border-charcoal/10 rounded-lg p-5 text-sm text-charcoal/70">
          The launch offer window has now closed. You can still claim your free basic listing below,
          or contact us about a full annual Bronze / Silver / Gold membership.
        </section>
      )}

      {/* Free basic path */}
      {!already_claimed && (
        <section className="mt-8 border-t border-charcoal/10 pt-6">
          <h3 className="font-serif text-lg text-charcoal mb-1">Not ready to upgrade?</h3>
          <p className="text-sm text-charcoal/70 mb-3">Claim your free basic listing (name, town, 1-line summary and map pin) —
            you&apos;ll get a partner account you can upgrade any time.
          </p>
          {basicOk?.ok ? (
            <div className="bg-forest/10 border border-forest/30 rounded p-4 text-sm">
              <CheckCircle2 className="inline text-forest mr-1" size={16}/> {basicOk.next}
            </div>
          ) : (
            <>
              <button onClick={() => setShowBasic((v) => !v)} data-testid="claim-basic-toggle"
                      className="text-sm text-heritage underline underline-offset-2 hover:text-heritage/70">
                {showBasic ? 'Cancel' : 'Claim my free basic listing →'}
              </button>
              {showBasic && (
                <form onSubmit={submitBasic} className="mt-4 space-y-3 max-w-md">
                  <input type="text" required placeholder="Your name"
                         value={basicForm.contact_name}
                         onChange={(e) => setBasicForm((f) => ({ ...f, contact_name: e.target.value }))}
                         className="w-full border border-charcoal/20 rounded px-3 py-2 text-sm"
                         data-testid="claim-basic-name"/>
                  <input type="email" required placeholder="Email"
                         value={basicForm.email}
                         onChange={(e) => setBasicForm((f) => ({ ...f, email: e.target.value }))}
                         className="w-full border border-charcoal/20 rounded px-3 py-2 text-sm"
                         data-testid="claim-basic-email"/>
                  {basicOk?.error && <p className="text-xs text-heritage">{basicOk.error}</p>}
                  <button type="submit" disabled={busy} data-testid="claim-basic-submit"
                          className="bg-charcoal text-cream px-6 py-2 rounded-full font-semibold text-sm hover:bg-charcoal/80 disabled:opacity-50">
                    {busy ? 'Sending…' : 'Send my claim to review'}
                  </button>
                </form>
              )}
            </>
          )}
        </section>
      )}

      <p className="text-xs text-charcoal/60 mt-10 border-t border-charcoal/10 pt-6">
        Membership options after this offer: Bronze £250/yr, Silver £500/yr, Gold £1,000/yr —
        Silver and Gold include priority placement + entry into the Great Days Out printed guide.
        Questions? Email <a href="mailto:hello@goleicestershire.com" className="underline">hello@goleicestershire.com</a>.
      </p>
    </main>
  );
}
